import { useCallback, useState } from "react";

import { BASEMAP_IDS, type BasemapId } from "../map/basemap";

const STORAGE_KEY = "kopateli.basemap";

/**
 * Which of the two basemaps is showing.
 *
 * This replaced the «Подписи» switch, which hid the Esri reference overlays. Those overlays
 * are gone together with the Esri basemap: the Google hybrid tile has its place names
 * printed into the raster, so there is nothing left to hide and a switch that does nothing
 * is worse than no switch. What a person actually needs from the two tiles is the choice
 * between them — the photograph to read the ground, the street map to work out how to get
 * there.
 *
 * Remembered next to the theme, and by the same rules: storage can be blocked, and a map
 * that will not open because of a preference is a worse map than one on the default tile.
 */
export const useBasemap = (): { basemap: BasemapId; setBasemap: (id: BasemapId) => void } => {
  const [basemap, setStored] = useState<BasemapId>(() => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return BASEMAP_IDS.includes(value as BasemapId) ? (value as BasemapId) : "hybrid";
    } catch {
      return "hybrid";
    }
  });

  const setBasemap = useCallback((id: BasemapId) => {
    setStored(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Not fatal: the choice still holds for this session.
    }
  }, []);

  return { basemap, setBasemap };
};
